import {
    CheckCircleIcon,
    EyeIcon,
    EyeSlashIcon,
    XCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { PASSWORD_RULE_COUNT, usePasswordForm } from '../hooks/usePasswordForm';

interface PasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (currentPassword: string, newPassword: string) => void;
}

interface PasswordFieldProps {
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    visible: boolean;
    onToggleVisible: () => void;
}

function PasswordField({
    label,
    placeholder,
    value,
    onChange,
    visible,
    onToggleVisible
}: PasswordFieldProps) {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-700">{label}</label>
            <div className="relative">
                <input
                    type={visible ? 'text' : 'password'}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    onClick={onToggleVisible}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                    {visible ? (
                        <EyeIcon className="w-4 h-4" />
                    ) : (
                        <EyeSlashIcon className="w-4 h-4" />
                    )}
                </button>
            </div>
        </div>
    );
}

function RuleItem({ label, valid }: { label: string; valid: boolean }) {
    return (
        <li className="flex items-center gap-2 text-sm">
            {valid ? (
                <CheckCircleIcon className="w-4 h-4 text-green-600" />
            ) : (
                <XCircleIcon className="w-4 h-4 text-gray-400" />
            )}
            <span className={valid ? 'text-gray-700' : 'text-gray-500'}>{label}</span>
        </li>
    );
}

function PasswordModal({ isOpen, onClose, onSave }: PasswordModalProps) {
    const form = usePasswordForm(isOpen);

    useEscapeKey(onClose, isOpen);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[520px] p-6 relative">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Set new password</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Close modal"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-col gap-4">
                    <PasswordField
                        label="Current password"
                        placeholder="Enter current password"
                        value={form.currentPassword}
                        onChange={form.setCurrentPassword}
                        visible={form.showCurrent}
                        onToggleVisible={() => form.setShowCurrent((s) => !s)}
                    />
                    <PasswordField
                        label="New password"
                        placeholder="Enter new password"
                        value={form.newPassword}
                        onChange={form.setNewPassword}
                        visible={form.showNew}
                        onToggleVisible={() => form.setShowNew((s) => !s)}
                    />
                    <PasswordField
                        label="Confirm new password"
                        placeholder="Re-enter new password"
                        value={form.confirmPassword}
                        onChange={form.setConfirmPassword}
                        visible={form.showConfirm}
                        onToggleVisible={() => form.setShowConfirm((s) => !s)}
                    />

                    <div className="mt-1">
                        <p className="text-xs font-semibold text-gray-500 mb-1">
                            Password strength
                        </p>
                        <div className="flex gap-1" aria-label="Password strength">
                            {Array.from({ length: PASSWORD_RULE_COUNT }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-1.5 flex-1 rounded ${idx < form.score ? form.strengthColor : 'bg-gray-200'}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-2">
                        <p className="text-xs font-semibold text-gray-500 mb-2">
                            Your password must contain:
                        </p>
                        <ul className="space-y-2">
                            {form.rules.map((rule) => (
                                <RuleItem key={rule.id} label={rule.label} valid={rule.valid} />
                            ))}
                            <RuleItem label="Passwords match" valid={form.passwordsMatch} />
                        </ul>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!form.allValid}
                        onClick={() => onSave(form.currentPassword, form.newPassword)}
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg transition-colors ${
                            form.allValid
                                ? 'bg-gray-900 hover:bg-gray-700'
                                : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PasswordModal;
